import { Box, Divider, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import { useMatchRoute } from '@tanstack/react-router';
import { ChevronDownIcon } from 'components/icons/ChevronDownIcon';
import { ChevronRightIcon } from 'components/icons/ChevronRightIcon';
import { NotifBadgeIcon } from 'components/notifBadgeIcon/NotifBadgeIcon';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import type { ReactElement } from 'react';
import { TrackedActions, useTrackEvent } from 'utils/tracking';
import ListItemLink from './ListItemLink';
import { ListItemStyle as ListItem, ListItemIconStyle, ListItemTextStyle } from './style';
import type { NavItemProps } from './type';

export function NavItemRoot({ item, open = false, active, onOpen }: NavItemProps) {
  const { isCollapse } = useCollapseDrawer();
  const trackEvent = useTrackEvent();

  const {
    title,
    tooltip,
    icon,
    info,
    caption,
    rightIcon,
    badgeCount,
    hasDivider,
    children,
    ...linkProps
  } = item;

  const trackExternalNavigate = () => {
    if (linkProps.href === 'https://intercom.help/faks/fr') {
      trackEvent(TrackedActions.Other.intercomFAQ);
    }
  };

  const collapseTooltipTitle = typeof title === 'string' ? title : undefined;

  const renderContent = (
    <>
      {icon && <NavItemIcon icon={icon} isCollapse={isCollapse} badgeCount={badgeCount} />}
      {!isCollapse && (
        <>
          <ListItemTextStyle
            disableTypography
            primary={title}
            secondary={<NavCaption caption={caption} />}
          />
          {!!badgeCount && <NotifBadgeIcon count={badgeCount} aria-hidden />}
          {rightIcon}
          {info && info}
          {children && <ArrowIcon open={open} />}
        </>
      )}
    </>
  );

  if (children) {
    return (
      <Tooltip title={isCollapse ? collapseTooltipTitle : undefined} placement="right">
        <ListItem
          onClick={onOpen}
          aria-expanded={open}
          activeRoot={active}
          disabled={linkProps.disabled}
          sx={{ ...(isCollapse && { px: 0 }) }}
        >
          {renderContent}
        </ListItem>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={isCollapse ? (tooltip ?? collapseTooltipTitle) : (tooltip ?? '')}
      placement={isCollapse ? 'right' : 'bottom-start'}
    >
      {linkProps.href ? (
        <ListItemLink target="_blank" rel="noopener" onClick={trackExternalNavigate} {...linkProps}>
          {renderContent}
        </ListItemLink>
      ) : (
        <Stack>
          <ListItemLink activeRoot={active} {...linkProps}>
            {renderContent}
          </ListItemLink>
          {!isCollapse && hasDivider && <Divider sx={{ m: 1 }} />}
        </Stack>
      )}
    </Tooltip>
  );
}

type NavItemSubProps = Omit<NavItemProps, 'isCollapse'>;

export function NavItemSub({ item, open = false, onOpen }: NavItemSubProps) {
  const { title, info, caption, rightIcon, badgeCount, tooltip, children, ...linkProps } = item;
  const matchRoute = useMatchRoute();
  const active = !!matchRoute({ fuzzy: true, includeSearch: false, ...linkProps });

  const renderContent = (
    <>
      <DotIcon active={active} />
      <ListItemText
        disableTypography
        primary={title}
        secondary={<NavCaption caption={caption} />}
      />
      {info && info}
      {!!badgeCount && <NotifBadgeIcon count={badgeCount} aria-hidden />}
      {rightIcon}
      {children && <ArrowIcon open={open} />}
    </>
  );

  if (children) {
    return (
      <ListItem onClick={onOpen} activeSub={active} subItem disabled={linkProps.disabled}>
        {renderContent}
      </ListItem>
    );
  }

  return (
    <Tooltip title={tooltip}>
      {linkProps.href ? (
        <ListItemLink target="_blank" rel="noopener" subItem {...linkProps}>
          {renderContent}
        </ListItemLink>
      ) : (
        <ListItemLink activeSub={active} subItem {...linkProps}>
          {renderContent}
        </ListItemLink>
      )}
    </Tooltip>
  );
}

type NavItemIconProps = {
  icon: ReactElement;
  isCollapse: boolean;
  badgeCount?: number;
};

function NavItemIcon({ icon, isCollapse, badgeCount }: NavItemIconProps) {
  return (
    <ListItemIconStyle
      sx={{
        position: 'relative',
        ...(isCollapse && { minWidth: 0, width: '100%', justifyContent: 'center' }),
      }}
    >
      {icon}
      {isCollapse && !!badgeCount && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: '50%',
            right: 'calc(50% - 25px)',
            transform: 'translateY(-50%)',
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'error.main',
            pointerEvents: 'none',
          }}
        />
      )}
    </ListItemIconStyle>
  );
}

type NavCaptionProps = {
  caption?: string;
};

function NavCaption({ caption }: NavCaptionProps) {
  return (
    <Tooltip title={caption || ''} arrow>
      <Typography
        noWrap
        variant="caption"
        component="div"
        sx={{ textTransform: 'initial', color: 'text.secondary' }}
      >
        {caption}
      </Typography>
    </Tooltip>
  );
}

type DotIconProps = {
  active: boolean;
};

export function DotIcon({ active }: DotIconProps) {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
            bgcolor: 'primary.main',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}

type ArrowIconProps = {
  open: boolean;
};

export function ArrowIcon({ open }: ArrowIconProps) {
  return open ? (
    <ChevronDownIcon sx={{ width: 16, height: 16, ml: 1 }} />
  ) : (
    <ChevronRightIcon sx={{ width: 16, height: 16, ml: 1 }} />
  );
}
